import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
  People as PeopleIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import DatabaseService from '../../services/DatabaseService';
import { useCurrency } from '../../context/CurrencyContext';

const Settings = () => {
  const { user } = useAuth();
  const { formatCurrency } = useCurrency();
  const [settings, setSettings] = useState({
    notifications: {
      emailNotifications: true,
      applicationAlerts: true,
      jobPostingUpdates: true,
      marketingEmails: false,
      weeklyReports: true
    },
    company: {
      autoApproveApplications: false,
      requireCoverLetter: true,
      allowRemoteWork: true,
      jobPostingDuration: '30'
    },
    billing: {
      plan: 'basic',
      autoRenewal: true,
      billingEmail: '',
      paymentMethod: 'credit_card'
    },
    team: {
      allowTeamAccess: false,
      maxTeamMembers: 5
    },
    account: {
      twoFactorAuth: false,
      sessionTimeout: '30'
    }
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passwordChangeDialog, setPasswordChangeDialog] = useState(false);
  const [addTeamMemberDialog, setAddTeamMemberDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [newTeamMember, setNewTeamMember] = useState({
    email: '',
    role: 'viewer',
    permissions: []
  });

  useEffect(() => {
    if (!user) return;

    const loadInitialSettings = async () => {
      try {
        setLoading(true);
        const userSettings = await DatabaseService.UserService.getSettings(user.id);
        if (userSettings) {
          setSettings(userSettings);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        showSnackbar('Error loading settings', 'error');
      } finally {
        setLoading(false);
      }
    };

    const loadInitialTeamMembers = async () => {
      try {
        const members = await DatabaseService.UserService.getTeamMembers(user.id);
        setTeamMembers(members || []);
      } catch (error) {
        console.error('Error loading team members:', error);
      }
    };

    loadInitialSettings();
    loadInitialTeamMembers();
  }, [user]);

  

  const loadTeamMembers = async () => {
    try {
      const members = await DatabaseService.UserService.getTeamMembers(user.id);
      setTeamMembers(members || []);
    } catch (error) {
      console.error('Error loading team members:', error);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      await DatabaseService.UserService.updateSettings(user.id, settings);
      showSnackbar('Settings saved successfully', 'success');
    } catch (error) {
      console.error('Error saving settings:', error);
      showSnackbar('Error saving settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      notifications: { ...prev.notifications, [field]: value }
    }));
  };

  const handleCompanyChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      company: { ...prev.company, [field]: value }
    }));
  };

  const handleBillingChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      billing: { ...prev.billing, [field]: value }
    }));
  };

  const handleTeamChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      team: { ...prev.team, [field]: value }
    }));
  };

  const handleAccountChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      account: { ...prev.account, [field]: value }
    }));
  };

  const handleAddTeamMember = async () => {
    try {
      setLoading(true);
      await DatabaseService.UserService.addTeamMember(user.id, newTeamMember);
      setAddTeamMemberDialog(false);
      setNewTeamMember({ email: '', role: 'viewer', permissions: [] });
      loadTeamMembers();
      showSnackbar('Team member added successfully', 'success');
    } catch (error) {
      console.error('Error adding team member:', error);
      showSnackbar('Error adding team member', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveTeamMember = async (memberId) => {
    try {
      await DatabaseService.UserService.removeTeamMember(user.id, memberId);
      loadTeamMembers();
      showSnackbar('Team member removed successfully', 'success');
    } catch (error) {
      console.error('Error removing team member:', error);
      showSnackbar('Error removing team member', 'error');
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showSnackbar('Passwords do not match', 'error');
      return;
    }

    try {
      setLoading(true);
      await DatabaseService.UserService.changePassword(
        user.id,
        passwordData.currentPassword,
        passwordData.newPassword
      );
      setPasswordChangeDialog(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showSnackbar('Password changed successfully', 'success');
    } catch (error) {
      console.error('Error changing password:', error);
      showSnackbar('Error changing password', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 4 }}>
        <SettingsIcon color="primary" />
        Company Settings
      </Typography>

      <Grid container spacing={4}>
        {/* Notification Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsIcon color="primary" />
                Notification Settings
              </Typography>
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.emailNotifications}
                      onChange={(e) => handleNotificationChange('emailNotifications', e.target.checked)}
                    />
                  }
                  label="Email Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.applicationAlerts}
                      onChange={(e) => handleNotificationChange('applicationAlerts', e.target.checked)}
                    />
                  }
                  label="Application Alerts"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.jobPostingUpdates}
                      onChange={(e) => handleNotificationChange('jobPostingUpdates', e.target.checked)}
                    />
                  }
                  label="Job Posting Updates"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.marketingEmails}
                      onChange={(e) => handleNotificationChange('marketingEmails', e.target.checked)}
                    />
                  }
                  label="Marketing Emails"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.weeklyReports}
                      onChange={(e) => handleNotificationChange('weeklyReports', e.target.checked)}
                    />
                  }
                  label="Weekly Reports"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Company Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Company Preferences
              </Typography>
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.company.autoApproveApplications}
                      onChange={(e) => handleCompanyChange('autoApproveApplications', e.target.checked)}
                    />
                  }
                  label="Auto-approve Applications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.company.requireCoverLetter}
                      onChange={(e) => handleCompanyChange('requireCoverLetter', e.target.checked)}
                    />
                  }
                  label="Require Cover Letter"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.company.allowRemoteWork}
                      onChange={(e) => handleCompanyChange('allowRemoteWork', e.target.checked)}
                    />
                  }
                  label="Allow Remote Work"
                />
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Job Posting Duration (days)</InputLabel>
                  <Select
                    value={settings.company.jobPostingDuration}
                    onChange={(e) => handleCompanyChange('jobPostingDuration', e.target.value)}
                    label="Job Posting Duration (days)"
                  >
                    <MenuItem value="7">7 days</MenuItem>
                    <MenuItem value="14">14 days</MenuItem>
                    <MenuItem value="30">30 days</MenuItem>
                    <MenuItem value="60">60 days</MenuItem>
                    <MenuItem value="90">90 days</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Billing Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PaymentIcon color="primary" />
                Billing Settings
              </Typography>
              <Box sx={{ mt: 2 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Subscription Plan</InputLabel>
                  <Select
                    value={settings.billing.plan}
                    onChange={(e) => handleBillingChange('plan', e.target.value)}
                    label="Subscription Plan"
                  >
                    <MenuItem value="basic">Basic - {formatCurrency(29)}/month</MenuItem>
                    <MenuItem value="professional">Professional - {formatCurrency(79)}/month</MenuItem>
                    <MenuItem value="enterprise">Enterprise - {formatCurrency(199)}/month</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Billing Email"
                  type="email"
                  value={settings.billing.billingEmail}
                  onChange={(e) => handleBillingChange('billingEmail', e.target.value)}
                  sx={{ mb: 2 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.billing.autoRenewal}
                      onChange={(e) => handleBillingChange('autoRenewal', e.target.checked)}
                    />
                  }
                  label="Auto-renewal"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Security Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SecurityIcon color="primary" />
                Security Settings
              </Typography>
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.account.twoFactorAuth}
                      onChange={(e) => handleAccountChange('twoFactorAuth', e.target.checked)}
                    />
                  }
                  label="Two-Factor Authentication"
                />
                <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
                  <InputLabel>Session Timeout (minutes)</InputLabel>
                  <Select
                    value={settings.account.sessionTimeout}
                    onChange={(e) => handleAccountChange('sessionTimeout', e.target.value)}
                    label="Session Timeout (minutes)"
                  >
                    <MenuItem value="15">15 minutes</MenuItem>
                    <MenuItem value="30">30 minutes</MenuItem>
                    <MenuItem value="60">1 hour</MenuItem>
                    <MenuItem value="120">2 hours</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setPasswordChangeDialog(true)}
                >
                  Change Password
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Team Management */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon color="primary" />
                  Team Management
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddTeamMemberDialog(true)}
                  disabled={!settings.team.allowTeamAccess}
                >
                  Add Team Member
                </Button>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.team.allowTeamAccess}
                    onChange={(e) => handleTeamChange('allowTeamAccess', e.target.checked)}
                  />
                }
                label="Allow Team Access"
                sx={{ mb: 2 }}
              />
              {settings.team.allowTeamAccess && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {teamMembers.map((member) => (
                        <TableRow key={member.id}>
                          <TableCell>{member.email}</TableCell>
                          <TableCell>{member.role}</TableCell>
                          <TableCell>{member.status}</TableCell>
                          <TableCell>
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveTeamMember(member.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {teamMembers.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            No team members added yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Save Settings */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" gap={2}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
              disabled={loading}
              size="large"
            >
              Save All Settings
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              size="large"
            >
              Delete Account
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Add Team Member Dialog */}
      <Dialog open={addTeamMemberDialog} onClose={() => setAddTeamMemberDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Team Member</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={newTeamMember.email}
            onChange={(e) => setNewTeamMember(prev => ({ ...prev, email: e.target.value }))}
            sx={{ mb: 2, mt: 1 }}
          />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={newTeamMember.role}
              onChange={(e) => setNewTeamMember(prev => ({ ...prev, role: e.target.value }))}
              label="Role"
            >
              <MenuItem value="viewer">Viewer</MenuItem>
              <MenuItem value="editor">Editor</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTeamMemberDialog(false)}>Cancel</Button>
          <Button onClick={handleAddTeamMember} variant="contained" disabled={loading}>
            Add Member
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={passwordChangeDialog} onClose={() => setPasswordChangeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="password"
            label="Current Password"
            value={passwordData.currentPassword}
            onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            type="password"
            label="New Password"
            value={passwordData.newPassword}
            onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="password"
            label="Confirm New Password"
            value={passwordData.confirmPassword}
            onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordChangeDialog(false)}>Cancel</Button>
          <Button onClick={handlePasswordChange} variant="contained" disabled={loading}>
            Change Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete your company account? This action cannot be undone and will remove all job postings and data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={loading}>
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Settings;